export const ORDER_TYPE_ENUM = {
  TAKE_AWAY: "TAKE-AWAY",
  DELIVERY: "DELIVERY",
  DINE_IN: "DINE-IN",
} as const;

export type OrderType = (typeof ORDER_TYPE_ENUM)[keyof typeof ORDER_TYPE_ENUM];

export const PLATFORM_ENUM = {
  WEB: "WEB",
  APP: "APP",
} as const;

export type Platform = (typeof PLATFORM_ENUM)[keyof typeof PLATFORM_ENUM];

/** e.g. `WEB_DELIVERY`, `APP_DINE-IN` — the shape the API keys shifts by. */
export type PlatformKey = `${Platform}_${OrderType}`;

export const STORE_STATUS_ENUM = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  BREAK: "BREAK",
} as const;

export type StoreStatus =
  (typeof STORE_STATUS_ENUM)[keyof typeof STORE_STATUS_ENUM];

export const ORDER_STATUS_ENUM = {
  UNCONFIRMED: "UNCONFIRMED",
  PLACED: "PLACED",
  PREPARING: "PREPARING",
  READY: "READY",
  OUT_FOR_DELIVERY: "OUT_FOR_DELIVERY",
  DELIVERED: "DELIVERED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
} as const;

export type OrderStatus =
  (typeof ORDER_STATUS_ENUM)[keyof typeof ORDER_STATUS_ENUM];

export const PAYMENT_METHOD_ENUM = {
  CASH: "CASH",
  CARD: "CARD",
} as const;

export type PaymentMethod =
  (typeof PAYMENT_METHOD_ENUM)[keyof typeof PAYMENT_METHOD_ENUM];

/**
 * `DELIVERY_AREAS_SEARCH_TYPE_ENUM` — verbatim from the shared enums module,
 * where it is declared as `((e.GOOGLE = "GOOGLE"), (e.MANUAL = "MANUAL"))`.
 *
 * It decides more than the delivery-address modal's map: the tracking page's
 * map (`ef`) is gated on it too, and a `MANUAL` tenant gets the "Live tracking
 * not available" stack instead of a Mapbox canvas.
 */
export const DELIVERY_AREAS_SEARCH_TYPE_ENUM = {
  GOOGLE: "GOOGLE",
  MANUAL: "MANUAL",
} as const;

export type DeliveryAreasSearchType =
  (typeof DELIVERY_AREAS_SEARCH_TYPE_ENUM)[keyof typeof DELIVERY_AREAS_SEARCH_TYPE_ENUM];

export const QUERY_PARAM_ENUM = {
  brandId: "brand_id",
  tenantId: "tenant_id",
  orderType: "order_type",
  floorId: "floor_id",
  tableId: "table_id",
  refreshCustomer: "refresh_customer",
} as const;

export const DAY_INDEX_ENUM = {
  MON: "MON",
  TUE: "TUE",
  WED: "WED",
  THU: "THU",
  FRI: "FRI",
  SAT: "SAT",
  SUN: "SUN",
} as const;

export type DayIndex = (typeof DAY_INDEX_ENUM)[keyof typeof DAY_INDEX_ENUM];

export const DAY_LABELS: Record<DayIndex, string> = {
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday",
  SUN: "Sunday",
};

/**
 * The reward types an offer or gift card can carry.
 *
 * These are the platform's own seven (`OFFER_REWARD_TYPE_ENUM`), and they live
 * in `@/types/wallet` beside the rest of the wallet domain. The six that used
 * to be declared here — `ITEM_DISCOUNT`, `FREE_ITEM`, `GIFT_CARD`, `MYSTERY` —
 * were invented and matched nothing the API sends.
 */
export { OFFER_REWARD_TYPE_ENUM } from "@/types/wallet";
export type { OfferRewardType } from "@/types/wallet";

/**
 * `PLATFORM_NAMES` — the original's display name per platform key, verbatim.
 *
 * The whole table is reproduced, not just the storefront's own `WEB_*` keys,
 * because a review or an order can arrive from any channel the tenant sells
 * through and both `platformLabel` and the reviews chip index straight into it.
 *
 * The Store Hours screen labels its tabs `PLATFORM_NAMES[key].replace("Web ", "")`
 * — "Delivery" / "Take-away" / "Dine-in". Note it does NOT honour
 * `takeaway_is_pickup`: the tab reads "Take-away" even for a pickup tenant.
 */
export const PLATFORM_NAMES: Record<string, string> = {
  POS_WALK_IN: "Pos Walk-in",
  "POS_TAKE-AWAY": "Pos Take-away",
  POS_DELIVERY: "Pos Delivery",
  "POS_DINE-IN": "Pos Dine-in",
  POS_DRIVE_THRU: "Pos Drive-thru",
  POS_KIOSK: "Pos Kiosk",
  "WEB_TAKE-AWAY": "Web Take-away",
  WEB_DELIVERY: "Web Delivery",
  "WEB_DINE-IN": "Web Dine-in",
  "APP_TAKE-AWAY": "App Take-away",
  APP_DELIVERY: "App Delivery",
  "APP_DINE-IN": "App Dine-in",
  "FOODPANDA_TAKE-AWAY": "Foodpanda Take-away",
  FOODPANDA_DELIVERY: "Foodpanda Delivery",
  "FOODPAPA_TAKE-AWAY": "Food-Papa Take-away",
  FOODPAPA_DELIVERY: "Food-Papa Delivery",
  "DOORDASH_TAKE-AWAY": "Doordash Take-away",
  DOORDASH_DELIVERY: "Doordash Delivery",
  "UBER-EATS_TAKE-AWAY": "Uber-Eats Take-away",
  "UBER-EATS_DELIVERY": "Uber-Eats Delivery",
  "MENU-LOG_TAKE-AWAY": "Menulog Take-away",
  "MENU-LOG_DELIVERY": "Menulog Delivery",
  "GFO_TAKE-AWAY": "GFO Take-away",
  GFO_DELIVERY: "GFO Delivery",
  "TALABAT_TAKE-AWAY": "Talabat Take-away",
  TALABAT_DELIVERY: "Talabat Delivery",
  "NOON_TAKE-AWAY": "Noon Take-away",
  NOON_DELIVERY: "Noon Delivery",
  "SMILES_TAKE-AWAY": "Smiles Take-away",
  SMILES_DELIVERY: "Smiles Delivery",
  "CAREEM_TAKE-AWAY": "Careem Take-away",
  CAREEM_DELIVERY: "Careem Delivery",
  "DELIVEROO_TAKE-AWAY": "Deliveroo Take-away",
  DELIVEROO_DELIVERY: "Deliveroo Delivery",
  "ZOMATO_TAKE-AWAY": "Zomato Take-away",
  ZOMATO_DELIVERY: "Zomato Delivery",
  "EATEASY_TAKE-AWAY": "EatEasy Take-away",
  EATEASY_DELIVERY: "EatEasy Delivery",
  "KEETA_TAKE-AWAY": "Keeta Take-away",
  KEETA_DELIVERY: "Keeta Delivery",
};

/**
 * `platformLabel` (the original's `y8`) — the SECOND word of the platform's
 * display name, which is the ordering mode on its own: `WEB_DELIVERY` reads
 * "Delivery", `FOODPANDA_TAKE-AWAY` reads "Take-away". It is what the reviews
 * list and the order history chip show, so a customer sees how they ordered
 * rather than which integration carried it.
 *
 * When the tenant calls take-away "pickup", the swap happens before the split.
 * The original gates that on membership of `PLATFORMS_BY_ORDER_TYPE[TAKE_AWAY]`,
 * which is exactly the set of names ending in "Take-away" — the test here is
 * the same set expressed against the name.
 */
export const platformLabel = (
  platform = "",
  takeawayIsPickup = false,
): string => {
  const name = PLATFORM_NAMES[platform] ?? "";
  const display =
    takeawayIsPickup && name.endsWith("Take-away")
      ? name.replace("Take-away", "Pickup")
      : name;
  return display.split(" ")[1] ?? "";
};

export const platformKey = (
  orderType: OrderType,
  platform: Platform = PLATFORM_ENUM.WEB,
): PlatformKey => `${platform}_${orderType}`;

export const titleCase = (value = ""): string =>
  value
    .toLowerCase()
    .split(/[\s\-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
